import React, { useState, useEffect } from "react";
import { Text, View, TouchableOpacity, Switch, ScrollView, Alert, Modal, Platform, Animated, SafeAreaView, Dimensions } from "react-native";
import { FontAwesome6 } from '@expo/vector-icons';
import { storeData, applyPreferences, DEFAULT_SETTINGS } from "./storage";
import { useTheme } from './ThemeContext';

const { width: screenWidth } = Dimensions.get("window");
const isSmallScreen = screenWidth < 375; // iPhone SE, iPhone 13 mini
const scaleFactor = isSmallScreen ? 0.85 : 1.0;

export default function Settings({ onNavigateBack, onRed670nmChange, red670nmEnabled }) {
  const [soundBellEnabled, setSoundBellEnabled] = useState(DEFAULT_SETTINGS.soundBellEnabled);
  const [selectedTheme, setSelectedTheme] = useState(DEFAULT_SETTINGS.theme);
  const [widerBeatRange, setWiderBeatRange] = useState(DEFAULT_SETTINGS.widerBeatRange);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastOpacity] = useState(new Animated.Value(0));
  const { changeTheme, themeValues } = useTheme();

  useEffect(() => {
    // Load saved settings on component mount
    const loadSettings = async () => {
      const settings = {
        soundBellEnabled,
        theme: selectedTheme,
        widerBeatRange
      };
      await applyPreferences(settings);

      // Update state with loaded values
      setSoundBellEnabled(settings.soundBellEnabled);
      setSelectedTheme(settings.theme);
      setWiderBeatRange(settings.widerBeatRange);
    };

    loadSettings();
  }, []);

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    Animated.sequence([
      Animated.timing(toastOpacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(toastOpacity, {
        toValue: 0,
        duration: 200,
        delay: 1500,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setShowToast(false);
    });
  };

  const handleBellToggle = async (value) => {
    setSoundBellEnabled(value);
    await storeData({ soundBellEnabled: value });
    showToastMessage('Settings saved ✓');
  };

  const handleThemeChange = async (theme) => {
    setSelectedTheme(theme);
    await storeData({ theme });
    // Apply theme change using ThemeContext
    await changeTheme(theme);
    console.log('Theme changed to:', theme);
    showToastMessage('Settings saved ✓');
  };

  const handleWiderBeatRangeToggle = async (value) => {
    const currentValue = widerBeatRange;
    setWiderBeatRange(value);

    // If turning off wider range and current BPM would be out of narrow range, clamp it
    if (!value && currentValue) {
      // User is turning off wider range - we need to check current BPM in main app
      // This will be handled by the main app component on next load
      // For now, let's update the stored BPM to be within narrow range
      try {
        const { getData, storeData } = await import('./storage');
        const settings = await getData();
        if (settings && settings.beatsPerMinute > 75) {
          await storeData({ beatsPerMinute: 75 });
        }
      } catch (error) {
        console.log('Error adjusting BPM for narrow range:', error);
      }
    }

    await storeData({ widerBeatRange: value });
    showToastMessage('Settings saved ✓');
  };

  const handleRed670nmToggle = async (value) => {
    if (onRed670nmChange) {
      onRed670nmChange(value);
    }
    await storeData({ red670nmEnabled: value });
    showToastMessage('Settings saved ✓');
  };

  const handleResetToFactory = () => {
    setShowResetDialog(true);
  };

  const confirmReset = async () => {
    try {
      // Reset all settings to defaults
      await storeData(DEFAULT_SETTINGS);
      // Update local state
      setSoundBellEnabled(DEFAULT_SETTINGS.soundBellEnabled);
      setSelectedTheme(DEFAULT_SETTINGS.theme);
      setWiderBeatRange(DEFAULT_SETTINGS.widerBeatRange);
      setShowResetDialog(false);
      if (onNavigateBack) {
        onNavigateBack();
      }
    } catch (e) {
      console.error("Failed to reset settings:", e);
    }
  };

  const themes = [
    { key: 'light', label: 'Light', icon: 'sun', color: '#FFD700' },
    { key: 'dark', label: 'Dark', icon: 'moon', color: '#4A5568' },
    { key: 'system', label: 'System', icon: 'desktop', color: '#718096' }
  ];

  return (
    <SafeAreaView
      style={{
        flex: 1,
        backgroundColor: themeValues.background,
        transform: [{ scale: scaleFactor }]
      }}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: themeValues.background,
          paddingTop: Platform.OS === 'ios' ? 60 : 50,
          paddingHorizontal: 20 * scaleFactor,
          paddingBottom: Platform.OS === 'android' || Platform.OS === 'ios' ? 20 : 0,
        }}
      >
      <Text
        style={{
          color: themeValues.primary,
          fontSize: 24 * scaleFactor,
          fontWeight: "bold",
          textAlign: "center",
          marginBottom: 15,
        }}
      >
        Settings
      </Text>

      <ScrollView style={{ flex: 1 }}>
        {/* Sound Bell Setting */}
        <View style={{
          backgroundColor: themeValues.secondary,
          borderRadius: 12,
          padding: 15 * scaleFactor,
          marginBottom: 15,
        }}>
          <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              flex: 1,
            }}>
              <FontAwesome6
                name="bell"
                size={20}
                color={soundBellEnabled ? themeValues.primary : themeValues.border}
                style={{ marginRight: 15, width: 25 }}
              />
              <Text style={{
                color: themeValues.text,
                fontSize: 16,
                flex: 1,
              }}>
                End Session Bell
              </Text>
            </View>
            <Switch
              value={soundBellEnabled}
              onValueChange={handleBellToggle}
              trackColor={{ false: themeValues.border, true: `${themeValues.primary}40` }}
              thumbColor={soundBellEnabled ? themeValues.primary : themeValues.muted}
            />
          </View>
        </View>

        {/* Wider Beat Range Setting */}
        <View style={{
          backgroundColor: themeValues.secondary,
          borderRadius: 12,
          padding: 15 * scaleFactor,
          marginBottom: 15,
        }}>
          <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              flex: 1,
            }}>
              <FontAwesome6
                name="heart-pulse"
                size={20}
                color={widerBeatRange ? themeValues.primary : themeValues.border}
                style={{ marginRight: 15, width: 25 }}
              />
              <View style={{ flex: 1 }}>
                <Text style={{
                  color: themeValues.text,
                  fontSize: 16,
                }}>
                  Wider Range of Beat Speed
                </Text>
                <Text style={{
                  color: themeValues.muted,
                  fontSize: 12,
                  marginTop: 2,
                }}>
                  {widerBeatRange ? '10-120 BPM (metronome range)' : '10-75 BPM (relaxation range)'}
                </Text>
              </View>
            </View>
            <Switch
              value={widerBeatRange}
              onValueChange={handleWiderBeatRangeToggle}
              trackColor={{ false: themeValues.border, true: `${themeValues.primary}40` }}
              thumbColor={widerBeatRange ? themeValues.primary : themeValues.muted}
            />
          </View>
        </View>

        {/* 670nm Red Light Setting */}
        <View style={{
          backgroundColor: themeValues.secondary,
          borderRadius: 12,
          padding: 15 * scaleFactor,
          marginBottom: 15,
        }}>
          <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              flex: 1,
            }}>
              <FontAwesome6
                name="eye"
                size={20}
                color={red670nmEnabled ? '#FF0000' : themeValues.border}
                style={{ marginRight: 15, width: 25 }}
              />
              <View style={{ flex: 1 }}>
                <Text style={{
                  color: themeValues.text,
                  fontSize: 16,
                }}>
                  670nm Red Light
                </Text>
                <Text style={{
                  color: themeValues.muted,
                  fontSize: 12,
                  marginTop: 2,
                }}>
                  Red light therapy may support better sleep and recovery
                </Text>
              </View>
            </View>
            <Switch
              value={red670nmEnabled}
              onValueChange={handleRed670nmToggle}
              trackColor={{ false: themeValues.border, true: 'rgba(255, 0, 0, 0.25)' }}
              thumbColor={red670nmEnabled ? '#FF0000' : themeValues.muted}
            />
          </View>
        </View>

        {/* Theme Setting */}
        <View style={{
          backgroundColor: themeValues.secondary,
          borderRadius: 12,
          padding: 15 * scaleFactor,
          marginBottom: 15,
        }}>
          <Text style={{
            color: themeValues.text,
            fontSize: 18,
            fontWeight: "600",
            marginBottom: 15,
            textAlign: 'center',
          }}>
            Theme
          </Text>
          <Text style={{
            color: themeValues.muted,
            fontSize: 14,
            marginBottom: 15,
          }}>
            Choose your preferred color theme
          </Text>

          {themes.map((theme) => (
            <TouchableOpacity
              key={theme.key}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: 12,
                paddingHorizontal: 15,
                backgroundColor: selectedTheme === theme.key ? 'rgba(255, 165, 0, 0.1)' : 'transparent',
                borderRadius: 8,
                borderWidth: selectedTheme === theme.key ? 1 : 0,
                borderColor: selectedTheme === theme.key ? 'orange' : 'transparent',
                marginBottom: 8,
              }}
              onPress={() => handleThemeChange(theme.key)}
            >
              <FontAwesome6
                name={theme.icon}
                size={20}
                color={theme.color}
                style={{ marginRight: 15, width: 25 }}
              />
              <Text style={{
                color: themeValues.text,
                fontSize: 16,
                flex: 1,
              }}>
                {theme.label}
              </Text>
              {selectedTheme === theme.key && (
                <FontAwesome6
                  name="check"
                  size={16}
                  color={themeValues.primary}
                />
              )}
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>

      {/* Action Button */}
      <View style={{
        paddingHorizontal: 20,
        paddingBottom: 20,
      }}>
        <TouchableOpacity
          style={{
            backgroundColor: 'rgba(255, 0, 0, 0.1)',
            borderWidth: 1,
            borderColor: 'red',
            borderRadius: 8,
            paddingVertical: 15,
            alignItems: 'center',
          }}
          onPress={handleResetToFactory}
        >
          <Text style={{
            color: 'red',
            fontSize: 16,
            fontWeight: '600',
          }}>
            Reset to Factory
          </Text>
        </TouchableOpacity>
      </View>

      <View style={{ paddingBottom: 20 }}>
        <Text
          style={{
            color: themeValues.muted,
            fontSize: 12,
            textAlign: "center",
          }}
        >
          © {new Date().getFullYear()} MeditRate. All rights reserved.
        </Text>
      </View>

      {/* Toast Notification */}
      {showToast && (
        <Animated.View
          style={{
            position: 'absolute',
            bottom: 100,
            left: 20,
            right: 20,
            backgroundColor: themeValues.primary,
            borderRadius: 8,
            padding: 15 * scaleFactor,
            opacity: toastOpacity,
            transform: [
              {
                translateY: toastOpacity.interpolate({
                  inputRange: [0, 1],
                  outputRange: [20, 0],
                }),
              },
            ],
          }}
        >
          <Text
            style={{
              color: 'white',
              fontSize: 16,
              fontWeight: '600',
              textAlign: 'center',
            }}
          >
            {toastMessage}
          </Text>
        </Animated.View>
      )}

      {/* Custom Themed Reset Dialog */}
      <Modal
        visible={showResetDialog}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowResetDialog(false)}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <View style={{
            backgroundColor: themeValues.secondary,
            borderRadius: 12,
            padding: 30 * scaleFactor,
            marginHorizontal: 40,
            alignItems: 'center',
            minWidth: 280,
          }}>
            <FontAwesome6
              name="exclamation"
              size={50}
              color="#FF6B6B"
              style={{ marginBottom: 20 }}
            />
            <Text style={{
              color: themeValues.text,
              fontSize: 20,
              fontWeight: '600',
              textAlign: 'center',
              marginBottom: 30 * scaleFactor,
            }}>
              Reset to Factory Defaults
            </Text>
            <Text style={{
              color: themeValues.muted,
              fontSize: 16,
              textAlign: 'center',
              marginBottom: 25 * scaleFactor,
              lineHeight: 22,
            }}>
              This will reset all settings to their default values. Are you sure?
            </Text>
            <View style={{
              flexDirection: 'row',
              gap: 15 * scaleFactor,
              width: '100%',
            }}>
              <TouchableOpacity
                style={{
                  flex: 1,
                  backgroundColor: themeValues.border,
                  paddingHorizontal: 20,
                  paddingVertical: 12,
                  borderRadius: 8,
                }}
                onPress={() => setShowResetDialog(false)}
              >
                <Text style={{
                  color: themeValues.text,
                  fontSize: 16,
                  fontWeight: '600',
                  textAlign: 'center',
                }}>
                  Cancel
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={{
                  flex: 1,
                  backgroundColor: '#FF6B6B',
                  paddingHorizontal: 20,
                  paddingVertical: 12,
                  borderRadius: 8,
                }}
                onPress={confirmReset}
              >
                <Text style={{
                  color: 'white',
                  fontSize: 16,
                  fontWeight: '600',
                  textAlign: 'center',
                }}>
                  Reset
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      </View>
    </SafeAreaView>
  );
}
